import * as React from 'react';
import { rem } from 'polished';
import { graphql } from 'gatsby';

import type { LinkType } from '../link';

import NaverCafeButton from './NaverCafeButton';
import TwitterButton from './TwitterButton';
import FacebookButton from './FacebookButton';
import { styled } from './themeContext';

type SiteNavigationProps = {
  socialType: NonNullable<GatsbyTypes.Footer_socialLinksFragment['snsLinks'][number]>['service'];
  link: LinkType,
  twitterButtonText: string,
  facebookButtonText: string,
  cafeButtonText: string,
  currentLocale: string,
};

const Container = styled.div(props => ({
  pointerEvents: 'none',
  position: 'fixed',
  top: rem(10),
  right: rem(10),
  zIndex: 1,
  transition: 'all .3s ease',
  '&.gameNavigationNotShown': {
    top: rem(50),
  },
  [props.theme.media['sm']]: {
    '&.gameNavigationNotShown': {
      top: rem(70),
    },
  },
  [props.theme.media['md']]: {
    '&.gameNavigationNotShown': {
      top: rem(80),
    },
  },
  [props.theme.media['lg']]: {
    width: '100%',
    maxWidth: rem(1200),
    top: rem(236),
    right: '50%',
    transform: 'translateX(50%)',
    '&.gameNavigationNotShown': {
      top: rem(236),
    },
  },
}));

const ButtonWrapper = styled.div(props => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  [props.theme.media['lg']]: {
    marginRight: rem(10),
  },
}));

const SiteNavigation: React.FC<SiteNavigationProps> = ({
  socialType,
  link,
  twitterButtonText,
  facebookButtonText,
  cafeButtonText,
  currentLocale,
}) => {
  const [gameNavigationNotShown, setGameNavigationNotShown] = React.useState(false);

  const handleScroll = React.useCallback(() => {
    if (window.pageYOffset > 60) {
      setGameNavigationNotShown(true);
    } else setGameNavigationNotShown(false);
  }, []);

  React.useEffect(() => {
    const isGameNavigationSupportedLanguage = !['de', 'fr'].includes(currentLocale);
    const watchScroll = () => { window.addEventListener("scroll", handleScroll) };
    if(!isGameNavigationSupportedLanguage) {
      setGameNavigationNotShown(true);
      return;
    }
    watchScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    }
  }, []);
  return (
    <Container className={gameNavigationNotShown ? "" : "gameNavigationNotShown"}>
      <ButtonWrapper>
        {(() => {
          switch (socialType) {
            case 'FACEBOOK':
              return <FacebookButton facebookLink={link} facebookButtonText={facebookButtonText} />;
            case 'TWITTER':
              return <TwitterButton twitterLink={link} twitterButtonText={twitterButtonText} />;
            case 'NAVER_CAFE':
              return <NaverCafeButton naverCafeLink={link} cafeButtonText={cafeButtonText} />;
            default:
              return null;
          }
        })()}
      </ButtonWrapper>
    </Container>
  );
};

export default SiteNavigation;

export const fragments = graphql`
  fragment SiteNavigation_socialType on SiteInformation {
    socialType
  }
`;
